import {useEffect, useState} from 'react';
import {Button, Table, Divider, Col, Row} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import {
  PageSizeArray,
  PaginationPosition,
  allEventRefundsFilters,
  formatCurrencyByLocalChapter,
  formatDate,
} from '../../../Util/commonUtility';
import {FilterOutlined} from '@ant-design/icons';
import DynamicSearchForm from '../../CommonComponents/DynamicSearch';
import AdminActionsDropdown from '../../CommonComponents/AdminActionsDropdown/AdminActionsDropdown';
import {getAllChapters} from '../../../Store/Actions';
import {DescriptionItem} from './../../../Util/commonUtility';
import AdminViewDetailsDrawer from './../../CommonComponents/AdminViewDetailsDrawer/index';
import {getAllEventsByQueryStringSearch} from '../../../Store/Actions/events';
import {usePermissions} from '../../../hooks/usePermission';
import useLocalization from '../../../Localization';
import useRefundApproval from '../../CommonComponents/RefundApproval/useRefundApproval';
import RefundApprovalFooter from '../../CommonComponents/RefundApproval/RefundApprovalFooter';

const AllRefundComponent = (props) => {
  let dispatch = useDispatch();
  const {
    allEventRefundData,
    paging,
    sortParams,
    handleTableChange,
    handlePaginationChange,
    fetchEventRefunds,
    searchValues,
  } = props;
  const [expanded, setExpanded] = useState(false);
  const [selectionType, setSelectionType] = useState('checkbox');
  const [isRefundAccessed, setIsRefundAccessed] = useState(true);

  const [open, setOpen] = useState(false);
  const [openDrawerDetails, setOpenDrawerDetails] = useState(false);
  const {ApplicationString} = useLocalization();

  // Event dropdown filter (searchable by event id or name) — mirrors CouponUsersTrack.
  const [eventsList, setEventsList] = useState([]);
  const [fetching, setFetching] = useState(false);
  const allEventsQueryData = useSelector(
    ({events}) => events.allEventsQueryData,
  );

  // A mixed-chapter selection is constrained to a single chapter at a time — each
  // chapter has its own currency and the refundable-base math needs one consistent chapter.
  const {
    showApproveReject,
    isModalVisible,
    refundToAccount,
    setRefundToAccount,
    percentageRefund,
    refundMode,
    exactAmountRefund,
    selectedRowTotalRefundAmount,
    selectedRowCreditAmount,
    selectedRowPaidAmount,
    selectedRowPaidAmountWithFee,
    selectedRowStripePaymentIds,
    selectedRowTotalDiscount,
    selectedRowLocalChapterId,
    isCreditOnlyRefund,
    rowSelection,
    handleRadioChange,
    handleRefundVisible,
    handleCancelRefund,
    hideModal,
    handleApproveRefunds,
    refundSubmitting,
    handleInputChange,
    selectedRowProcessingFee,
    selectedRowProcessingFeePercentage,
    handleRefundAmountChange,
    handleRefundModeChange,
  } = useRefundApproval({filterByLocalChapter: true});

  const allChapters = useSelector((state) => state.chapters.allChapters);
  const selectedRowLocalChapterData = allChapters?.data?.chapters?.find(
    (obj) => obj.id === selectedRowLocalChapterId,
  );
  const getRecordLocalChapterData = (record) => {
    return allChapters?.data?.chapters?.find(
      (obj) => obj.id === record?.local_chapter_id,
    );
  };
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const userRole = userInfo?.role;
  const {checkPermission} = usePermissions(userRole);

  useEffect(() => {
    if (!checkPermission('events_refund', 'refundRegistration')) {
      setIsRefundAccessed(false);
    }
  }, []);
  useEffect(() => {
    if (!allChapters?.data?.chapters) {
      dispatch(getAllChapters());
    }
  }, [allChapters]);

  useEffect(() => {
    dispatch(
      getAllEventsByQueryStringSearch({page: 1, pagecount: 100}, '', '', ''),
    );
  }, []);

  // Map the searchable events response into the dropdown's option list.
  useEffect(() => {
    if (allEventsQueryData?.forSearch) {
      const events = allEventsQueryData.forSearch?.data?.events || [];
      setEventsList(
        events.map((event) => ({
          value: event.id,
          label:
            event.id != null && event.name != null
              ? `${event.id} - ${event.name}`
              : event.name || String(event.id),
        })),
      );
      setFetching(false);
    }
  }, [allEventsQueryData?.forSearch]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    searchValues && setExpanded(true);
  }, [searchValues]);

  // Drawer functions ----------------------------------------------------
  const showDrawer = (record) => {
    setOpenDrawerDetails(record);
    setOpen(true);
  };

  const onCloseDrawer = () => {
    setOpen(false);
  };

  const EventDrawerContent = () => {
    const {
      attendee_first_name,
      attendee_last_name,
      user_id,
      event_id,
      attendee_email,
      attendee_company,
      is_sponsor,
      is_reps,
      registration_type,
      event_registration_options,
      total_cost,
      discount_amount,
      discount_code,
      is_processing_fee_added,
      selectedRowProcessingFee,
      selectedRowProcessingFeePercentage,
      status,
      created_at,
      credit_amount,
      transaction_id,
    } = openDrawerDetails || {};
    const drawerLocalChapterData = getRecordLocalChapterData(openDrawerDetails);

    return (
      <>
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Attendee Status'
              content={
                {
                  3: 'Paid',
                  4: 'Payment Confirmed',
                  6: 'Fee Wavier',
                  '-1': 'To be Deleted',
                  0: 'Payment Refunded',
                  1: 'To be Paid',
                  2: 'Refund Pending',
                  5: 'No Charge Event',
                }[status] || status
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='User Id' content={user_id} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='Event Id' content={event_id} />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='First Name' content={attendee_first_name} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='Last Name' content={attendee_last_name} />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='Attendee Email' content={attendee_email} />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Attendee Company'
              content={attendee_company}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Sponsorship'
              content={is_sponsor ? 'Yes' : 'No'}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Representative'
              content={is_reps ? 'Yes' : 'No'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Registration Type'
              content={registration_type}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Registration Option'
              content={
                event_registration_options?.sponsorship?.name
                  ? event_registration_options?.sponsorship?.name
                  : event_registration_options?.registration?.name +
                    ' ' +
                    '-' +
                    ' ' +
                    formatCurrencyByLocalChapter(
                      event_registration_options?.registration?.price,
                      drawerLocalChapterData,
                    )
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Food Option'
              content={
                event_registration_options?.food?.name
                  ? event_registration_options?.food?.name +
                    ' ' +
                    '-' +
                    ' ' +
                    formatCurrencyByLocalChapter(
                      event_registration_options?.food?.price,
                      drawerLocalChapterData,
                    )
                  : 'No Food'
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Total Cost'
              content={formatCurrencyByLocalChapter(
                total_cost ? total_cost : 0,
                drawerLocalChapterData,
              )}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Used Credit'
              content={formatCurrencyByLocalChapter(
                credit_amount ? credit_amount : 0,
                drawerLocalChapterData,
              )}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Discounted Cost'
              content={formatCurrencyByLocalChapter(
                discount_amount ? discount_amount : 0,
                drawerLocalChapterData,
              )}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Total Cost After Discount'
              content={formatCurrencyByLocalChapter(
                total_cost - discount_amount || 0,
                drawerLocalChapterData,
              )}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Coupon Code Applied'
              content={discount_code || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Registered Date'
              content={formatDate(created_at)}
            />
          </Col>
        </Row>
        <Col span={12}>
          <DescriptionItem
            title='Processing Fee Added'
            content={
              is_processing_fee_added
                ? 'Yes (paid by attendee)'
                : 'No (absorbed by org)'
            }
          />
        </Col>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Stripe Payment ID'
              content={transaction_id || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
      </>
    );
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
  };
  const handleSearch = (values) => {
    fetchEventRefunds(values);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      sorter: true,
      width: '4%',
    },
    {
      title: 'Event Name',
      dataIndex: 'event_name',
      sorter: true,
      width: '16%',
      render: (text) => {
        return <span>{text || 'N/A'}</span>;
      },
    },
    {
      title: 'First Name',
      dataIndex: 'attendee_first_name',
      sorter: true,
      width: '10%',
      render: (text, record) => {
        return <span>{text || 'N/A'}</span>;
      },
    },
    {
      title: 'Last Name',
      dataIndex: 'attendee_last_name',
      sorter: true,
      width: '10%',
      render: (text, record) => {
        return <span>{text || 'N/A'}</span>;
      },
    },
    {
      title: 'Email',
      dataIndex: 'attendee_email',
      sorter: true,
      width: '15%',
      ellipsis: true,
      render: (text) => {
        return <span>{text || 'N/A'}</span>;
      },
    },
    {
      title: 'Registration Option',
      dataIndex: 'registration_option',
      width: '15%',
      render: (text, record) => {
        const isOptionsSponsor =
          record.event_registration_options?.sponsorship?.name;
        return (
          <span>
            {/* {checkIsEditable(record)} */}
            {record?.is_reps || record?.is_sponsor
              ? 'N/A'
              : isOptionsSponsor
              ? isOptionsSponsor
              : record.event_registration_options?.registration?.name +
                ' ' +
                '-' +
                ' ' +
                formatCurrencyByLocalChapter(
                  record?.event_registration_options?.registration?.price,
                  getRecordLocalChapterData(record),
                )}
          </span>
        );
      },
    },
    {
      title: 'Food Option',
      dataIndex: 'food',
      width: '15%',
      ellipsis: true,
      render: (text, record) => {
        return (
          <span>
            {record.event_registration_options?.food?.name
              ? record.event_registration_options?.food?.name +
                ' ' +
                '-' +
                ' ' +
                formatCurrencyByLocalChapter(
                  record?.event_registration_options?.food?.price,
                  getRecordLocalChapterData(record),
                )
              : 'No Food'}
          </span>
        );
      },
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      width: '10%',
      ellipsis: true,
      sorter: true,
      render: (text, record) => {
        return (
          <span>
            {formatCurrencyByLocalChapter(
              text ? text : 0,
              getRecordLocalChapterData(record),
            )}
          </span>
        );
      },
    },
    {
      title: 'Refund Amount',
      dataIndex: 'refunded_amount',
      width: '10%',
      ellipsis: true,
      sorter: true,
      render: (text, record) => {
        return (
          <span>
            {formatCurrencyByLocalChapter(
              text,
              getRecordLocalChapterData(record),
            )}
          </span>
        );
      },
    },
    {
      title: 'Refund Mode',
      dataIndex: 'refund_to_account',
      width: '10%',
      ellipsis: true,
      sorter: true,
      render: (text, record) => {
        let refundMode;
        //Added specific condition for true and false because initially show NA when refund is approved the show Direct or Credit
        if (record?.status === 'Refund Pending') {
          refundMode = 'N/A';
        } else {
          refundMode = text ? 'Credit' : 'Direct';
        }
        return <span>{refundMode}</span>;
      },
    },
    {
      title: 'Stripe Payment ID',
      dataIndex: 'transaction_id',
      width: '14%',
      ellipsis: true,
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      fixed: 'right',
      width: '9%',
      render: (text) => {
        return <span>{text}</span>;
      },
    },
    {
      title: '',
      key: 'action',
      fixed: 'right',
      width: '3%',
      render: (_, record) => (
        <AdminActionsDropdown
          text='Event Refund'
          record={record}
          viewBool={true}
          handleView={showDrawer}
          resource='events_refund'
          localChapterData={getRecordLocalChapterData(record)}
        />
      ),
    },
  ];
  columns.forEach((column) => {
    if (column.key !== 'action') {
      column.sortOrder =
        sortParams.field === column.dataIndex && sortParams.order;
    }
  });

  return (
    <div className='main-container'>
      <AdminViewDetailsDrawer
        content={EventDrawerContent}
        visible={open}
        text={'Event Refund'}
        onCloseDrawer={onCloseDrawer}
        isEditable={false}
      />
      <div className='search-container' style={{gap: '10px'}}>
        <Button onClick={toggleExpand} id='EventRefund-button-filterSearch'>
          {expanded ? <FilterOutlined /> : <FilterOutlined />}{' '}
          {ApplicationString['dynamicSearch.button.heading']}
        </Button>
      </div>
      {expanded && (
        <DynamicSearchForm
          columns={allEventRefundsFilters}
          onSearch={handleSearch}
          savedFilteredValues={searchValues}
          title='all_event_refunds'
          eventsList={eventsList}
          setEventsList={setEventsList}
          fetching={fetching}
          setFetching={setFetching}
        />
      )}
      <div className='listing-container'>
        <Table
          bordered
          className='admin-table'
          columns={columns}
          dataSource={allEventRefundData}
          rowKey={'id'}
          onChange={handleTableChange}
          scroll={{x: 2100}}
          sticky={true}
          rowSelection={
            isRefundAccessed
              ? {
                  type: selectionType,
                  ...rowSelection,
                }
              : undefined
          }
          footer={() => (
            <div>
              <RefundApprovalFooter
                showApproveReject={showApproveReject}
                isModalVisible={isModalVisible}
                hideModal={hideModal}
                refundSubmitting={refundSubmitting}
                handleRadioChange={handleRadioChange}
                refundToAccount={refundToAccount}
                setRefundToAccount={setRefundToAccount}
                handleInputChange={handleInputChange}
                percentageRefund={percentageRefund}
                refundMode={refundMode}
                handleRefundModeChange={handleRefundModeChange}
                selectedRowProcessingFee={selectedRowProcessingFee}
                selectedRowProcessingFeePercentage={
                  selectedRowProcessingFeePercentage
                }
                exactAmountRefund={exactAmountRefund}
                handleRefundAmountChange={handleRefundAmountChange}
                handleApproveRefunds={handleApproveRefunds}
                selectedRowTotalRefundAmount={selectedRowTotalRefundAmount}
                selectedRowCreditAmount={selectedRowCreditAmount}
                selectedRowPaidAmount={selectedRowPaidAmount}
                selectedRowPaidAmountWithFee={selectedRowPaidAmountWithFee}
                selectedRowStripePaymentIds={selectedRowStripePaymentIds}
                selectedRowTotalDiscount={selectedRowTotalDiscount}
                localChapterData={selectedRowLocalChapterData}
                handleRefundVisible={handleRefundVisible}
                handleCancelRefund={handleCancelRefund}
                creditOnlyRefund={isCreditOnlyRefund}
              />
            </div>
          )}
          pagination={{
            onChange: (page, pageSize) => {
              handlePaginationChange(page, pageSize);
            },
            current: paging.page,
            total: paging.totalCount,
            pageSize: paging.pageCount,
            pageSizeOptions: PageSizeArray,
            showSizeChanger: true,
            showQuickJumper: true,
            onShowSizeChange: (page, pageSize) => {
              handlePaginationChange(page, pageSize);
            },
            showTotal: (total, range) => `Total ${total} items`,
            position: [PaginationPosition],
          }}
        />
      </div>
    </div>
  );
};

export default AllRefundComponent;
